/**
 * Reception mode on this device: ONE store every screen reads (lock-as-role,
 * 2026-10-02).
 *
 * The lock is on this device's sign-in (its token): while locked the server
 * narrows user.access to the person's real access ∩ the business's Front desk
 * role, and refuses everything else. GET /reception-mode/settings answers for
 * THIS device with the lock's caps and an areas VIEW of them (`hiddenAreas`),
 * which the older screens and the route guards read. This store only decides
 * what to SHOW, and it fails CLOSED:
 *  - the first answer each page load comes from the server; until it does, a
 *    device last known to be locked is treated as locked;
 *  - if the server can't be reached, a device last known to be locked stays
 *    locked (with the last known areas, or everything but the diary, the till
 *    and finding a client);
 *  - `receptionLockedDevice` survives a location switch (which clears the
 *    other site caches) — the lock does too.
 *
 * Area keys mirror beu-node-v2 receptionCaps.AREA_CAP.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config/API';
import { loadAccess } from '../component/permissions/access';
import { subscribe as followSync, poke as pokeSync, getArea as syncArea, versionOf as syncVersionOf } from './syncSummary';

export const CACHE_KEY = 'receptionModeSettings'; // { enabled, hiddenAreas, caps } while locked (older screens read it)
const LOCK_KEY = 'receptionLockedDevice';         // '1' while last known locked
const EVENT = 'receptionModeSettingsChanged';

export const AREA_KEYS = [
    'diary', 'checkout', 'clients', 'clientDetails', 'clientMessages', 'transactions', 'refunds', 'cashup',
    'catalogue', 'services', 'products', 'productData', 'productOrders', 'productStore',
    'giftcards', 'voucherDesigns', 'voucherSales', 'giftcardAdmin', 'stocktake', 'forms', 'taxes', 'resources',
    'staff', 'teamMembers', 'schedules', 'staffPay',
    'settings', 'payoutAccount', 'cardReader', 'salonDetails', 'bookingUrl', 'billing',
    'locations', 'aiReceptionist', 'diarySharing', 'notificationSettings', 'importExport', 'clientsExport',
    'finishSetup', 'profile', 'accountPassword',
    'marketing', 'bailey', 'baileyApprovals', 'discounts', 'permissions'
];

// Used only when the device is known to be locked but we can't say what it
// hides: everything except the diary, the till and finding a client.
const LOCKED_FALLBACK = AREA_KEYS.reduce((o, k) => { o[k] = !['diary', 'checkout', 'clients'].includes(k); return o; }, {});

let state = null;      // { enabled, hiddenAreas, hairdresserId } — verified with the server this page load
let inflight = null;
let following = null;
let loadedVersion = null;

function _read(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
function _write(key, v) { try { if (v === null) localStorage.removeItem(key); else localStorage.setItem(key, v); } catch (e) { /* ignore */ } }
function _cache() { try { return JSON.parse(_read(CACHE_KEY) || 'null'); } catch (e) { return null; } }

function _normalise(h) {
    const out = {};
    AREA_KEYS.forEach((k) => { out[k] = !!(h && h[k] === true); });
    out.diary = false;
    return out;
}

/** The best current answer: verified, else the last known one (fail closed). */
export function getState() {
    if (state) return Object.assign({ verified: true }, state);
    const cached = _cache();
    if (cached && cached.enabled) return { enabled: true, hiddenAreas: _normalise(cached.hiddenAreas), verified: false };
    if (_read(LOCK_KEY) === '1') return { enabled: true, hiddenAreas: Object.assign({}, LOCKED_FALLBACK), verified: false };
    return { enabled: false, hiddenAreas: _normalise(null), verified: false };
}

export function isLocked() { return getState().enabled === true; }

/** Is any of `areas` (a key or a list) hidden on this device right now? */
export function isHidden(areas) {
    const s = getState();
    if (!s.enabled) return false;
    const list = Array.isArray(areas) ? areas : [areas];
    return list.some((a) => s.hiddenAreas[a] === true);
}

// ---- Bailey and setup on a locked device ----------------------------------
// Bailey is OFF at the desk unless the owner switched it on (Settings ›
// Reception); setup ("Set up with Bailey", the welcome, the checklist) is
// never shown on a locked device, whatever the switches say.

/** Every Bailey surface hidden: locked, and the desk has no Bailey. */
export function baileyOff() { return isLocked() && isHidden('bailey'); }
/** Approving what Bailey proposed is not allowed here (locked without approvals). */
export function approvalsOff() { return isLocked() && isHidden(['bailey', 'baileyApprovals']); }
/** Setup is never on a locked device. */
export function setupOff() { return isLocked(); }

/** React: this device's reception state, re-rendering when it changes. */
export function useReception() {
  const [s, setS] = useState(getState);
  useEffect(() => {
    const unsub = subscribe(() => setS(getState()));
    setS(getState());
    return unsub;
  }, []);
  return {
    locked: s.enabled === true,
    hiddenAreas: s.hiddenAreas,
    baileyOff: s.enabled === true && s.hiddenAreas.bailey === true,
    approvalsOff: s.enabled === true && (s.hiddenAreas.bailey === true || s.hiddenAreas.baileyApprovals === true),
    setupOff: s.enabled === true,
  };
}

function _apply(next) {
    const prev = state;
    state = next;
    if (next.enabled) {
        _write(CACHE_KEY, JSON.stringify({ enabled: true, hiddenAreas: next.hiddenAreas, caps: next.caps || null }));
        _write(LOCK_KEY, '1');
    } else {
        _write(CACHE_KEY, null);
        _write(LOCK_KEY, null);
    }
    const changed = !prev || prev.enabled !== next.enabled || JSON.stringify(prev.hiddenAreas) !== JSON.stringify(next.hiddenAreas);
    if (changed) {
        try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { enabled: next.enabled, hiddenAreas: next.hiddenAreas } })); } catch (e) { /* ignore */ }
    }
}

/** Adopt a GET /reception-mode/settings answer (or a write's answer). */
export function applyServer(data) {
    if (!data) return getState();
    const device = data.device || null;
    const locked = device ? device.locked === true : data.enabled === true;
    _apply({
        enabled: locked,
        hiddenAreas: _normalise(locked ? ((device && device.hiddenAreas) || data.hiddenAreas || LOCKED_FALLBACK) : null),
        caps: locked && device && Array.isArray(device.caps) ? device.caps : null,
        roleKey: locked && device ? device.roleKey || null : null,
        hairdresserId: (device && device.hairdresserId) || data.hairdresserId || null
    });
    return getState();
}

function _headers() {
    return { 'Content-Type': 'application/json', authorization: 'Basic YmV1YXBwOmJldWFwcA==', accessToken: _read('accessToken') };
}

/** Ask the server. Deduplicated; never rejects. */
export function refresh() {
    if (!_read('accessToken')) { _apply({ enabled: false, hiddenAreas: _normalise(null), hairdresserId: null }); return Promise.resolve(getState()); }
    if (inflight) return inflight;
    // The routine poll's version of these settings as of NOW (before asking):
    // a change landing mid-request shows up as a difference next poll.
    loadedVersion = syncVersionOf(syncArea('reception'));
    inflight = axios.get(config.RECEPTION_MODE_SETTINGS, { headers: _headers() })
        .then((res) => {
            const body = res && res.data;
            if (body && body.statusCode === 1) return applyServer(body.responseData);
            throw new Error((body && body.error && body.error.responseMessage) || 'reception settings unavailable');
        })
        .catch(() => {
            // Not current after all: the next poll asks again.
            loadedVersion = null;
            // Fail CLOSED when this device was last known to be locked.
            const known = getState();
            if (known.enabled) _apply({ enabled: true, hiddenAreas: known.hiddenAreas, hairdresserId: state && state.hairdresserId });
            else if (!state) _apply({ enabled: false, hiddenAreas: _normalise(null), hairdresserId: null });
            return getState();
        })
        .then((s) => { inflight = null; _startRevalidate(); return s; });
    return inflight;
}

/** Verified state: resolves at once if this page load already asked. */
export function ensure() {
    return state ? Promise.resolve(getState()) : refresh();
}

/**
 * Keep it current. The app's routine poll (/sync/summary) carries a version
 * of this device's settings; they are asked for again only when it moves
 * (it used to be every 60s regardless). Focus asks the poll at once, so a
 * lock or unlock in another tab still shows straight away. The poll pauses in
 * a hidden tab and asks the moment it's back. Against an older server (no
 * /sync/summary) the poll falls back to the old 60s cadence (up to 2 min).
 */
function _startRevalidate() {
    if (following || typeof window === 'undefined') return;
    following = followSync({
        areas: ['reception'],
        onUpdate: (data, event) => {
            if (event.error) return;
            const v = data.reception;
            if (!v) return;
            if (v.legacy) { if (event.changed.includes('reception')) refresh(); return; }
            if (v.version !== loadedVersion) refresh();
        }
    });
    window.addEventListener('focus', () => { pokeSync(); });
}

/** Subscribe to changes; returns an unsubscribe. */
export function subscribe(fn) {
    const h = (e) => fn(e && e.detail);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
}

/** Error text from a reception-mode API failure (400 refusals included). */
export function errorText(err, fallback) {
    const d = (err && err.response && err.response.data) || err || {};
    return (d.error && d.error.responseMessage) || d.message || fallback || 'Something went wrong';
}

function _post(url, body) {
    return axios.post(url, body, { headers: _headers() }).then((res) => {
        const data = res && res.data;
        if (!data || data.statusCode !== 1) {
            const e = new Error((data && data.error && data.error.responseMessage) || 'Something went wrong');
            e.response = { data };
            throw e;
        }
        return data.responseData;
    });
}

/** After a lock change: re-read the lock and the narrowed user.access. */
function _afterChange() {
    return refreshNow().then((s) => Promise.resolve(loadAccess && loadAccess()).catch(() => null).then(() => s));
}
function refreshNow() { inflight = null; state = null; return refresh(); }

/** Lock THIS device (the PIN proves whoever locks it can unlock it). */
export function lock(pin) {
    return _post(config.RECEPTION_MODE_LOCK, { pin }).then(_afterChange);
}

/** Unlock THIS device with the PIN. Rejects with the server's message. */
export function unlock(pin) {
    return _post(config.RECEPTION_MODE_UNLOCK, { pin }).then(() => {
        _apply({ enabled: false, hiddenAreas: _normalise(null), caps: null, hairdresserId: null });
        return _afterChange();
    });
}

/** First PIN; locks this device too unless `lockNow` is false. */
export function setupPin(pin, lockNow) {
    return _post(config.RECEPTION_MODE_SETUP_PIN, { pin, confirmPin: pin, lock: lockNow !== false }).then(_afterChange);
}

/** Forget everything (sign-out). */
export function clearReception() {
    state = null;
    _write(CACHE_KEY, null);
    _write(LOCK_KEY, null);
}
